import * as Sentry from "@sentry/browser";
import { Amplify } from "aws-amplify";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { CookieStorage } from "aws-amplify/utils";
import { commitLocalUpdate, graphql } from "react-relay";
import { createOperationDescriptor, getRequest } from "relay-runtime";

import { authCookieHandler, getAuthCookieOptions } from "app/utils/auth";
import { listenToAuth } from "app/utils/authHub";
import { getConfigs, SamlProvidersArraySchema } from "app/utils/configs";
import { createUrlConfig } from "app/utils/localState";
import { failOAuthCallback } from "app/utils/oauthCallback";
import { signInRedirectHandler } from "app/utils/redirection";

import { RelayEnvironment } from "./RelayEnvironment";

import type { AuthHubCapsule } from "app/utils/authHub";
import type { ResourcesConfig } from "aws-amplify";
import type { Environment } from "relay-runtime";
import type { Primitive, Record } from "relay-runtime/store/RelayStoreTypes";

interface CognitoConfig {
  region: Primitive | Primitive[];
  userPoolId: Primitive | Primitive[];
  userPoolWebClientId: Primitive | Primitive[];
  authenticationFlowType: Primitive | Primitive[];
  cognitoInstall?: Primitive | Primitive[];
  saml: Primitive | Primitive[];
  samlProviders?: Primitive | Primitive[];
}

type CognitoConfigRecord = Record<CognitoConfig> | null | undefined;

async function createCognitoConfig(environment: Environment) {
  const config = (await getConfigs()).cognito;

  commitLocalUpdate(environment, async (store) => {
    const __typename = "Cognito";

    const record = store.create(__typename, __typename);
    record.setValue(config.cognito_user_pool_client_id, "userPoolWebClientId");
    record.setValue(config.cognito_user_pool_id, "userPoolId");
    record.setValue(config.cognito_user_pool_region, "region");
    record.setValue(config.cognito_install, "cognitoInstall");
    record.setValue(JSON.stringify(config.saml), "saml");
    if (config.saml_providers) {
      record.setValue(JSON.stringify(config.saml_providers), "samlProviders");
    }
    record.setValue("USER_SRP_AUTH", "authenticationFlowType");
    store.getRoot().setLinkedRecord(record, __typename);
  });

  // The graphql queries against local data require a ref to at least 1 server definition. It can be empty, so we use __typename
  const localDataQuery = graphql`
    query bootstrapCognitoQuery {
      Cognito {
        __typename
      }
      platform {
        __typename
      }
    }
  `;

  const request = getRequest(localDataQuery);
  const operation = createOperationDescriptor(request, {});
  environment.retain(operation);
  return;
}

/**
 * The scopes the hosted-UI redirect asks Cognito for. The two `stacklet/*`
 * scopes are the resource-server scopes the platform and Redash APIs check,
 * and `aws.cognito.signin.user.admin` is what the platform client allows
 * (`allowed_oauth_scopes` in platform's cognito.tf).
 */
const OAUTH_SCOPES = [
  "email",
  "openid",
  "aws.cognito.signin.user.admin",
  "stacklet/platform",
  "stacklet/assetdb",
];

type CognitoUserPoolConfig = Extract<
  NonNullable<NonNullable<ResourcesConfig["Auth"]>["Cognito"]>,
  { userPoolClientId: string }
>;

/**
 * Builds Amplify's resource config from the Cognito record in the Relay
 * store. Pure apart from reporting a malformed SAML config, so the OAuth
 * round-trip test can run the real SDK against exactly this config.
 */
export function createAmplifyConfig(
  config: NonNullable<CognitoConfigRecord>,
): ResourcesConfig {
  const cognito: CognitoUserPoolConfig = {
    userPoolId: String(config.userPoolId),
    userPoolClientId: String(config.userPoolWebClientId),
  };

  // Check if SAML providers are configured to enable OAuth
  // Try new saml_providers array first, fallback to legacy saml dict
  let hasSamlProviders = false;

  try {
    if (typeof config.samlProviders === "string") {
      const parsed = JSON.parse(config.samlProviders);
      const result = SamlProvidersArraySchema.safeParse(parsed);

      if (result.success && result.data.length > 0) {
        hasSamlProviders = true;
      } else if (!result.success) {
        console.error(
          "SAML providers validation failed in bootstrap:",
          result.error.errors,
        );
        Sentry.captureException(
          new Error("SAML providers validation failed in bootstrap"),
          {
            // Report the shape of the fault, not the SAML config itself. The
            // provider names and IdP ids belong to the customer.
            extra: {
              validationErrors: result.error.errors.map((issue) => ({
                path: issue.path,
                code: issue.code,
                message: issue.message,
              })),
            },
          },
        );
      }
    }

    // Fall back to legacy saml dict if new format not present or invalid
    if (
      !hasSamlProviders &&
      typeof config.saml === "string" &&
      config.saml !== "{}"
    ) {
      hasSamlProviders = true;
    }
  } catch (error) {
    console.error("Failed to parse SAML configuration in bootstrap:", error);
    Sentry.captureException(
      new Error("Failed to parse SAML configuration in bootstrap"),
      {
        // The SAML config itself stays out of the report: it holds customer
        // provider names and IdP ids, and the parse error names the fault.
        extra: { error: String(error) },
      },
    );
    // Fall back to checking legacy saml dict without parsing
    hasSamlProviders = typeof config.saml === "string" && config.saml !== "{}";
  }

  if (hasSamlProviders) {
    cognito.loginWith = {
      oauth: {
        domain: String(config.cognitoInstall),
        scopes: OAUTH_SCOPES,
        redirectSignIn: [window.origin],
        redirectSignOut: [window.origin],
        // The platform client allows only the implicit grant
        // (`allowed_oauth_flows` in platform's cognito.tf).
        responseType: "token",
      },
    };
  }

  return { Auth: { Cognito: cognito } };
}

/**
 * Points Amplify at the user pool, with Cognito's session in cookies on the
 * parent domain so the sibling services (Redash, Superset, Sinistral) can read
 * it.
 *
 * The storage goes in after configure, which resets the token provider to
 * localStorage every time it runs. That order is safe for an SSO callback:
 * configure starts completing it from the `configure` event, but the SDK
 * reads its own state before it writes any token, and that read yields, so
 * the cookie storage is in place first. The OAuth round-trip test holds the
 * SDK to this.
 */
export function applyAmplifyConfig(resources: ResourcesConfig): void {
  Amplify.configure(resources);

  cognitoUserPoolsTokenProvider.setKeyValueStorage(
    new CookieStorage(getAuthCookieOptions()),
  );
}

/** Configures Amplify and subscribes the console to its auth events. */
export function installAmplify(resources: ResourcesConfig): void {
  listenToAuth(authHandler);
  applyAmplifyConfig(resources);
}

async function configureAmplify(environment: Environment) {
  const cognitoConfig: CognitoConfigRecord = environment
    .getStore()
    .getSource()
    ?.get("Cognito");

  if (cognitoConfig) installAmplify(createAmplifyConfig(cognitoConfig));
}

async function authHandler(data: AuthHubCapsule) {
  // The auth machine ignores hosted-UI failures, so nothing else would stop
  // the console waiting on a redirect that already failed.
  if (data.payload.event === "signInWithRedirect_failure") {
    failOAuthCallback();
  }

  // auth cookie must be set before redirect happens
  await authCookieHandler();
  await signInRedirectHandler(data);
}

async function createDeploymentConfig(environment: Environment) {
  const config = (await getConfigs()).api;

  commitLocalUpdate(environment, async (store) => {
    const __typename = "Deployment";

    const record = store.create(__typename, __typename);
    record.setValue(config.url, "gqlUri");
    if (config.headers) {
      record.setValue(JSON.stringify(config.headers), "headers");
    }
    store.getRoot().setLinkedRecord(record, __typename);
  });

  // The graphql queries against local data require a ref to at least 1 server definition. It can be empty, so we use __typename
  const localDataQuery = graphql`
    query bootstrapDeploymentQuery {
      Deployment {
        __typename
      }
      platform {
        __typename
      }
    }
  `;

  const request = getRequest(localDataQuery);
  const operation = createOperationDescriptor(request, {});
  environment.retain(operation);
  return;
}

async function bootstrapStacklet(): Promise<void> {
  await createDeploymentConfig(RelayEnvironment);
  await createCognitoConfig(RelayEnvironment);
  await configureAmplify(RelayEnvironment);
  await createUrlConfig(RelayEnvironment);
}

export { bootstrapStacklet };

export type { CognitoConfigRecord };
